// Threaded comments panel for a resume. One level of nesting: replies
// attach to the top-level parent (backend collapses reply-to-reply).
// Author can delete their own; super admin can delete any.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageSquare, Send, Trash2, CornerDownRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined,
  { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export function CommentsPanel({ resumeId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null); // {comment_id, author_name}

  const reload = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/jobs/resumes/${resumeId}/comments`);
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [resumeId]);

  const post = async () => {
    const body = draft.trim();
    if (!body) return;
    try {
      setBusy(true);
      await api.post(`/jobs/resumes/${resumeId}/comments`, {
        body, parent_id: replyTo?.comment_id || null,
      });
      setDraft(""); setReplyTo(null);
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Post failed");
    } finally { setBusy(false); }
  };

  const del = async (cid) => {
    try {
      await api.delete(`/jobs/resumes/${resumeId}/comments/${cid}`);
      toast.success("Deleted");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const isSuper = user?.role === "super_admin";
  const parents = items.filter((c) => !c.parent_id);
  const repliesFor = (cid) => items.filter((c) => c.parent_id === cid);
  const canDelete = (c) => isSuper || c.author_user_id === user?.user_id;

  return (
    <div className="border-t border-slate-100 bg-slate-50/40 p-4 space-y-3"
         data-testid={`resume-comments-${resumeId}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        <MessageSquare className="h-3.5 w-3.5" /> Recruiter notes ({items.length})
      </div>
      {loading ? (
        <div className="py-3"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
      ) : parents.length === 0 ? (
        <div className="text-xs text-slate-400">
          No notes yet. Drop your first take below.
        </div>
      ) : (
        <div className="space-y-2">
          {parents.map((c) => (
            <div key={c.comment_id} className="rounded-md bg-white border border-slate-100 p-2.5"
                 data-testid={`comment-${c.comment_id}`}>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="font-semibold text-slate-800">{c.author_name || "—"}</span>
                <span className="text-slate-300">·</span>
                <span>{fmt(c.created_at)}</span>
                <div className="ml-auto inline-flex gap-1">
                  <button onClick={() => { setReplyTo({ comment_id: c.comment_id, author_name: c.author_name }); }}
                          data-testid={`comment-reply-${c.comment_id}`}
                          className="text-[10px] font-medium text-primary hover:underline">
                    Reply
                  </button>
                  {canDelete(c) && (
                    <button onClick={() => del(c.comment_id)}
                            data-testid={`comment-delete-${c.comment_id}`}
                            className="text-[10px] font-medium text-rose-500 hover:underline">
                      <Trash2 className="inline h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{c.body}</div>
              {repliesFor(c.comment_id).map((r) => (
                <div key={r.comment_id} className="mt-2 ml-4 rounded-md bg-slate-50 border border-slate-100 p-2"
                     data-testid={`comment-${r.comment_id}`}>
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                    <CornerDownRight className="h-3 w-3 text-slate-300" />
                    <span className="font-semibold text-slate-800">{r.author_name || "—"}</span>
                    <span className="text-slate-300">·</span>
                    <span>{fmt(r.created_at)}</span>
                    {canDelete(r) && (
                      <button onClick={() => del(r.comment_id)}
                              data-testid={`comment-delete-${r.comment_id}`}
                              className="ml-auto text-rose-500 hover:underline">
                        <Trash2 className="inline h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{r.body}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <div className="pt-1">
        {replyTo && (
          <div className="mb-1 flex items-center gap-2 text-[11px] text-slate-500">
            <CornerDownRight className="h-3 w-3" />
            Replying to <b>{replyTo.author_name || "note"}</b>
            <button onClick={() => setReplyTo(null)} className="text-primary hover:underline">cancel</button>
          </div>
        )}
        <div className="flex gap-2">
          <Textarea rows={2} value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); post(); } }}
                    placeholder="Add a note — ⌘/Ctrl+Enter to post"
                    data-testid={`comment-input-${resumeId}`}
                    className="text-sm" />
          <Button onClick={post} disabled={busy || !draft.trim()}
                  data-testid={`comment-post-${resumeId}`}
                  className="self-end gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Post
          </Button>
        </div>
      </div>
    </div>
  );
}
